import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ProjectList from '../components/ProjectList';
import ProjectGrid from '../components/ProjectGrid';
import MagneticButton from '../components/MagneticButton'; 

import { LayoutGrid } from 'lucide-react';
import { projectsData } from '../data/projectsData';

const VscMenu = () => (
  <svg 
    stroke="currentColor" 
    fill="currentColor" 
    strokeWidth="0" 
    viewBox="0 0 16 16" 
    height="1em" 
    width="1em" 
    xmlns="http://www.w3.org/2000/svg"
    style={{ display: 'block' }} 
  >
    <path d="M1 2.75A.75.75 0 0 1 1.75 2h12.5a.75.75 0 0 1 0 1.5H1.75A.75.75 0 0 1 1 2.75zm0 5A.75.75 0 0 1 1.75 7h12.5a.75.75 0 0 1 0 1.5H1.75A.75.75 0 0 1 1 7.75zm0 5A.75.75 0 0 1 1.75 12h12.5a.75.75 0 0 1 0 1.5H1.75a.75.75 0 0 1-.75-.75z" />
  </svg>
);

export default function Work() {
  const [view, setView] = useState('grid');
  const [filter, setFilter] = useState('All');

  const filteredProjects = projectsData.filter(project => {
    if (filter === 'All') return true;
    return project.category === filter;
  });

  return (
    <div className='bg-gray-100 min-h-screen'>
      <div className="pb-20 pt-64">
        
        <section className="text-black max-w-340 w-full px-4 md:px-24 mx-auto font-['Montserrat',sans-serif] mb-28">
          <h1 className="text-4xl sm:text-5xl md:text-8xl leading-tight font-medium [letter-spacing:-1px] md:[letter-spacing:-2px] capitalize">
            You are currently <br className="hidden md:block" /> Exploring My Portfolio
          </h1>
        </section>

        <section className='flex max-w-340 w-full px-4 md:px-24 mx-auto justify-between relative text-black mb-20 md:mb-42'>
          <div className='font-medium gap-3 md:gap-4 flex flex-wrap'>
            <MagneticButton 
              onClick={() => setFilter('All')}
              className={`px-6 py-4 md:px-10 md:py-8 text-sm md:text-base rounded-full transition-colors border ${
                filter === 'All' 
                  ? 'bg-black border-transparent text-white' 
                  : 'border-zinc-600 text-black hover:bg-indigo-600 hover:text-white hover:border-transparent'
              }`}
            >
              All
            </MagneticButton>
            
            <MagneticButton 
              onClick={() => setFilter('Website')}
              className={`px-6 py-4 md:px-10 md:py-8 text-sm md:text-base rounded-full transition-colors border ${
                filter === 'Website' 
                  ? 'bg-black border-transparent text-white' 
                  : 'border-zinc-600 text-black hover:bg-indigo-600 hover:text-white hover:border-transparent'
              }`}
            >
              Website<sup className="text-xs text-zinc-400 ml-0.5 font-normal">{projectsData.filter(p => p.category === 'Website').length}</sup>
            </MagneticButton>
            
            <MagneticButton 
              onClick={() => setFilter('Design')}
              className={`px-6 py-4 md:px-10 md:py-8 text-sm md:text-base rounded-full transition-colors border ${
                filter === 'Design' 
                  ? 'bg-black border-transparent text-white' 
                  : 'border-zinc-600 text-black hover:bg-indigo-600 hover:text-white hover:border-transparent'
              }`}
            >
              Design<sup className="text-xs text-zinc-400 ml-0.5 font-normal">{projectsData.filter(p => p.category === 'Design').length}</sup>
            </MagneticButton>
          </div>

          <div className='hidden md:flex relative gap-4'>
            <MagneticButton 
              onClick={() => setView('list')} 
              className={`border px-8 text-2xl rounded-full transition-colors ${
                view === 'list' 
                  ? 'bg-black border-black text-white' 
                  : 'border-black text-black hover:bg-indigo-600 hover:text-white hover:border-transparent'
              }`}
            >
              <VscMenu />
            </MagneticButton>
            <MagneticButton 
              onClick={() => setView('grid')} 
              className={`border px-8 text-2xl rounded-full transition-colors ${
                view === 'grid' 
                  ? 'bg-black border-black text-white' 
                  : 'border-black text-black hover:bg-indigo-600 hover:text-white hover:border-transparent'
              }`}
            >
              <LayoutGrid />
            </MagneticButton>
          </div>
        </section>

        <div className="w-full overflow-hidden">
          {/* mode="wait" ensures the current view exits before the new one enters */}
          <AnimatePresence mode="wait">
            {view === 'list' ? (
              <motion.div
                key={`list-${filter}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
              >
                <div className="hidden md:block">
                  <ProjectList projects={filteredProjects} />
                </div>
                <div className="block md:hidden">
                  <ProjectGrid projects={filteredProjects} />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key={`grid-${filter}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="w-full"
              >
                <ProjectGrid projects={filteredProjects} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}